import { useParams } from 'react-router-dom'
import { useCustomPage } from '../../hooks/useCustomPages'
import { useEventContext } from '../../contexts/EventContext'
import { useAccount } from '../../../hooks/useAccount'
import { useEventDetails } from '../../../hooks/useEventDetails'
import { SectionRenderer } from '../sections/SectionRenderer'
import { Section } from '../../types/customPage'
import { Lock, UserCheck } from 'lucide-react'
import { useEffect } from 'react'

export const CustomPage = () => {
    const { pageSlug } = useParams<{ pageSlug: string }>()
    const { eventSlug } = useEventContext()
    const { data: page, isLoading, error } = useCustomPage(eventSlug, pageSlug!)
    const { data: account } = useAccount()
    const { data: eventDetails } = useEventDetails(eventSlug)

    // Update page title with custom page name
    useEffect(() => {
        if (page && eventDetails) {
            document.title = `${page.title} - ${eventDetails.name}`;
        }
    }, [page, eventDetails]);

    if (isLoading) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="animate-pulse">
                    <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-8"></div>
                    <div className="space-y-4">
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                    </div>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h2 className="text-red-800 text-lg font-semibold mb-2">Error Loading Page</h2>
                    <p className="text-red-600">
                        {error instanceof Error ? error.message : 'An error occurred while loading the page.'}
                    </p>
                </div>
            </div>
        )
    }

    if (!page) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <h2 className="text-yellow-800 text-lg font-semibold mb-2">Page Not Found</h2>
                    <p className="text-yellow-600">
                        The requested page could not be found.
                    </p>
                </div>
            </div>
        )
    }

    // Check if user has access to this page
    const hasAccess = checkAccess(page, account, eventDetails)

    if (!hasAccess.allowed) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-12">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                    <div className="flex justify-center mb-4">
                        {hasAccess.reason === 'account' ? (
                            <Lock className="h-12 w-12 text-gray-400" />
                        ) : (
                            <UserCheck className="h-12 w-12 text-gray-400" />
                        )}
                    </div>
                    <h2 className="text-gray-800 text-xl font-semibold mb-2">
                        {hasAccess.reason === 'account' ? 'Login Required' : 'Registration Required'}
                    </h2>
                    <p className="text-gray-600 mb-4">
                        {hasAccess.reason === 'account'
                            ? 'You must be logged in to view this page.'
                            : 'You must be registered for this event to view this page.'}
                    </p>
                    {hasAccess.reason === 'account' ? (
                        <a
                            href="/login"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Login to Continue
                        </a>
                    ) : (
                        <a
                            href={`/${eventSlug}/registration`}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Register for Event
                        </a>
                    )}
                </div>
            </div>
        )
    }

    // Transform sections for SectionRenderer
    const transformedSections = transformSections(page.sections || [])

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            {/* Content sections */}
            <div className="space-y-8">
                {transformedSections.map((section) => (
                    <SectionRenderer
                        key={section.id}
                        section={section}
                    />
                ))}
            </div>
        </div>
    )
}

// Helper functions
function checkAccess(page: any, account: any, eventDetails: any) {
    // Check page-level requirements
    if (page.requireAccount && !account) {
        return { allowed: false, reason: 'account' }
    }

    if (page.requireRegistration) {
        const isRegistered = eventDetails?.currentUserRegistered || false
        if (!isRegistered) {
            return { allowed: false, reason: 'registration' }
        }
    }

    return { allowed: true }
}

function transformSections(sections: Section[]) {
    return sections.map(section => ({
        id: section.id.toString(),
        type: section.typeOf as any, // Map typeOf to type
        title: section.customTitle || '',
        content: section.details,
        visible: true,
        order: section.position,
        fullWidth: section.sectionOption?.fullWidth || false,
        attachments: section.attachments?.map(att => ({
            id: att.id,
            name: att.title,
            fileUrl: att.imageUrl,
            url: att.imageUrl,
            thumbnailUrl: att.imageThumbUrl,
            caption: att.description
        }))
    }))
}